import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FindInPage as FindInPageIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useJob } from '../contexts/JobContext';
import { inventoryApi } from '../services/api.service';

/**
 * TransferHistory Component
 * 
 * Displays a history of equipment transfers between jobs
 * Allows filtering and searching for specific transfers
 */
const TransferHistory = () => {
  // State for transfers data
  const [transfers, setTransfers] = useState([]);
  const [filteredTransfers, setFilteredTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [filterJobId, setFilterJobId] = useState('');
  const [filterTimeframe, setFilterTimeframe] = useState('all');
  
  // Get context data
  const { jobs } = useJob();
  
  // Load transfer history data
  useEffect(() => {
    const fetchTransferHistory = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Mock implementation for demo
        // In a real implementation, this would call an API endpoint
        const mockTransfers = [
          {
            id: 'transfer-1',
            transferDate: new Date().toISOString(),
            sourceJob: { id: 'J001', name: 'Downtown Hotel AV' },
            destinationJob: { id: 'J002', name: 'Conference Center Setup' },
            items: [
              { id: 'PROJ-001', description: 'Projector HD', quantity: 2 },
              { id: 'MIC-002', description: 'Wireless Microphone', quantity: 5 }
            ],
            reason: 'Equipment needed urgently',
            transferredBy: 'john.doe@example.com',
            notes: 'Conference center event was short on equipment'
          },
          {
            id: 'transfer-2',
            transferDate: new Date(Date.now() - 86400000).toISOString(), // Yesterday
            sourceJob: { id: 'J003', name: 'Corporate Event' },
            destinationJob: { id: 'J001', name: 'Downtown Hotel AV' },
            items: [
              { id: 'SPKR-004', description: 'Powered Speaker', quantity: 4 }
            ],
            reason: 'Job priority changed',
            transferredBy: 'jane.smith@example.com',
            notes: 'Corporate event postponed'
          },
          {
            id: 'transfer-3',
            transferDate: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
            sourceJob: { id: 'J002', name: 'Conference Center Setup' },
            destinationJob: { id: 'J004', name: 'Wedding Reception' },
            items: [
              { id: 'LIGHT-002', description: 'LED Par Lights', quantity: 8 },
              { id: 'MIXER-001', description: 'Audio Mixer', quantity: 1 }
            ],
            reason: 'Equipment needed urgently',
            transferredBy: 'tech.support@example.com',
            notes: 'Wedding reception needed additional lighting'
          },
          {
            id: 'transfer-4',
            transferDate: new Date(Date.now() - 604800000).toISOString(), // 1 week ago
            sourceJob: { id: 'J005', name: 'Theater Production' },
            destinationJob: { id: 'J003', name: 'Corporate Event' },
            items: [
              { id: 'CAM-003', description: 'Video Camera', quantity: 2 },
              { id: 'TRIPOD-001', description: 'Camera Tripod', quantity: 2 }
            ],
            reason: 'Equipment no longer needed',
            transferredBy: 'admin@example.com',
            notes: 'Theater production finished early'
          }
        ];
        
        // In a real implementation, we would fetch from the API:
        // const response = await inventoryApi.getTransferHistory();
        // if (response && response.data) {
        //   setTransfers(response.data);
        //   setFilteredTransfers(response.data);
        // }
        
        setTransfers(mockTransfers);
        setFilteredTransfers(mockTransfers);
      } catch (err) {
        console.error('Error loading transfer history:', err);
        setError('Failed to load transfer history. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTransferHistory();
  }, []);
  
  // Apply filters when criteria change
  useEffect(() => {
    let filtered = [...transfers];
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(transfer => 
        transfer.sourceJob.name.toLowerCase().includes(query) ||
        transfer.destinationJob.name.toLowerCase().includes(query) ||
        transfer.sourceJob.id.toLowerCase().includes(query) ||
        transfer.destinationJob.id.toLowerCase().includes(query) ||
        transfer.items.some(item => 
          item.id.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
        )
      );
    }
    
    // Filter by job ID
    if (filterJobId) {
      filtered = filtered.filter(transfer => 
        transfer.sourceJob.id === filterJobId ||
        transfer.destinationJob.id === filterJobId
      );
    }
    
    // Filter by timeframe
    const now = new Date();
    if (filterTimeframe === 'today') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      filtered = filtered.filter(transfer => 
        new Date(transfer.transferDate).getTime() >= today
      );
    } else if (filterTimeframe === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).getTime();
      filtered = filtered.filter(transfer => 
        new Date(transfer.transferDate).getTime() >= weekAgo
      );
    } else if (filterTimeframe === 'month') {
      const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()).getTime();
      filtered = filtered.filter(transfer => 
        new Date(transfer.transferDate).getTime() >= monthAgo
      );
    }
    
    setFilteredTransfers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [transfers, searchQuery, filterJobId, filterTimeframe]);
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterJobId('');
    setFilterTimeframe('all');
  };
  
  // Handle data refresh
  const handleRefresh = async () => {
    // Reload the transfer history data
    setLoading(true);
    
    // In a real implementation, we would fetch fresh data:
    // try {
    //   const response = await inventoryApi.getTransferHistory();
    //   if (response && response.data) {
    //     setTransfers(response.data);
    //     setFilteredTransfers(response.data);
    //   }
    // } catch (err) {
    //   console.error('Error refreshing transfer history:', err);
    //   setError('Failed to refresh transfer history.');
    // } finally {
    //   setLoading(false);
    // }
    
    // For this demo, just simulate a delay
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };
  
  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Transfer History
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        View a history of equipment transfers between jobs. Use the filters to narrow down results.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Filters and Search */}
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Transfers"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by job name, equipment ID, etc."
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="job-filter-label">Filter by Job</InputLabel>
              <Select
                labelId="job-filter-label"
                value={filterJobId}
                onChange={(e) => setFilterJobId(e.target.value)}
                label="Filter by Job"
              >
                <MenuItem value="">
                  <em>All Jobs</em>
                </MenuItem>
                <MenuItem value="J001">Downtown Hotel AV (J001)</MenuItem>
                <MenuItem value="J002">Conference Center Setup (J002)</MenuItem>
                <MenuItem value="J003">Corporate Event (J003)</MenuItem>
                <MenuItem value="J004">Wedding Reception (J004)</MenuItem>
                <MenuItem value="J005">Theater Production (J005)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="timeframe-filter-label">Timeframe</InputLabel>
              <Select
                labelId="timeframe-filter-label"
                value={filterTimeframe}
                onChange={(e) => setFilterTimeframe(e.target.value)}
                label="Timeframe"
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={1}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleClearFilters}
              disabled={!searchQuery && !filterJobId && filterTimeframe === 'all'}
              startIcon={<FilterIcon />}
              size="medium"
              sx={{ px: 2 }}
            >
              Clear
            </Button>
          </Grid>
          
          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleRefresh}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              size="medium"
              sx={{ px: 2 }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Transfer History Table */}
      <Paper elevation={2} sx={{ width: '100%', mb: 2, p: 0 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : filteredTransfers.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No transfer records found with the current filters.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleClearFilters}
                sx={{ mt: 2, px: 2 }}
                size="small"
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
            <Table stickyHeader aria-label="transfer history table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Transferred By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransfers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transfer) => (
                    <TableRow key={transfer.id} hover>
                      <TableCell>
                        {formatDate(transfer.transferDate)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transfer.sourceJob.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transfer.sourceJob.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transfer.destinationJob.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transfer.destinationJob.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {transfer.items.map((item, idx) => (
                            <Chip
                              key={`${transfer.id}-${item.id}-${idx}`}
                              size="small"
                              label={`${item.quantity}× ${item.id}`}
                              title={item.description}
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                          <Typography variant="caption" color="text.secondary">
                            {transfer.items.length} item{transfer.items.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {transfer.reason}
                        {transfer.notes && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Note: {transfer.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {transfer.transferredBy}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <Divider />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredTransfers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Paper>
  );
};

export default TransferHistory;